import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr, fmtDate } from "../api";
import { StatusChip, Btn, ConfirmDialog, Modal, Field, inputCls } from "../components";

const blank = { title: "", location: "", type: "Full-time", description: "", responsibilities: [], requirements: [] };

const CareersAdmin = () => {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null); // {doc, data, respText, reqText}
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.get("/jobs").then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const r = await adminApi.post("/jobs", { title: "New Job Opening" });
      openEdit(r.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const openEdit = async (doc) => {
    const full = await adminApi.get(`/jobs/${doc.id}`);
    setEdit({
      doc: full.data,
      data: full.data.data,
      respText: (full.data.data.responsibilities || []).join("\n"),
      reqText: (full.data.data.requirements || []).join("\n"),
      skillText: (full.data.data.skills || []).join("\n"),
    });
  };

  const save = async (andPublish = false) => {
    setBusy(true);
    try {
      const payload = {
        ...edit.data,
        responsibilities: edit.respText.split("\n").map((s) => s.trim()).filter(Boolean),
        requirements: edit.reqText.split("\n").map((s) => s.trim()).filter(Boolean),
        skills: (edit.skillText || "").split("\n").map((s) => s.trim()).filter(Boolean),
      };
      await adminApi.put(`/jobs/${edit.doc.id}`, payload);
      if (andPublish) await adminApi.post(`/jobs/${edit.doc.id}/publish`);
      toast.success(andPublish ? "Job published — live on the Careers page" : "Draft saved");
      setEdit(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const togglePublish = async (j) => {
    try {
      await adminApi.post(`/jobs/${j.id}/${j.status === "published" ? "unpublish" : "publish"}`);
      toast.success(j.status === "published" ? "Job unpublished" : "Job published");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await adminApi.delete(`/jobs/${del.id}`);
      toast.success("Job deleted");
      setDel(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-careers-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Careers</h1>
          <p className="mt-1 text-sm text-slate-500">Published openings appear on the public Careers page. Applications arrive as enquiries.</p>
        </div>
        <Btn onClick={create} data-testid="add-job-btn"><Plus size={14} /> Add Job Opening</Btn>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              {["Job Title", "Location", "Status", "Updated", "Actions"].map((h) => (
                <th key={h} className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items === null ? (
              <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">No job openings yet.</td></tr>
            ) : (
              items.map((j) => (
                <tr key={j.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`job-row-${j.id}`}>
                  <td className="px-6 py-3 font-semibold text-slate-900">{j.data?.title}</td>
                  <td className="px-6 py-3 text-slate-600">{j.data?.location || "—"}</td>
                  <td className="px-6 py-3"><StatusChip status={j.status} /></td>
                  <td className="px-6 py-3 text-xs text-slate-500">{fmtDate(j.updated_at)}</td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => openEdit(j)} className="p-2 text-slate-500 hover:text-[#C5221F]" data-testid={`edit-job-${j.id}`}><Pencil size={15} /></button>
                      <Btn variant="ghost" className="!px-2.5 !py-1.5" onClick={() => togglePublish(j)} data-testid={`toggle-job-${j.id}`}>{j.status === "published" ? "Unpublish" : "Publish"}</Btn>
                      <button onClick={() => setDel(j)} className="p-2 text-slate-400 hover:text-red-600" data-testid={`delete-job-${j.id}`}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title="Edit Job Opening" wide>
        {edit && (
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Job Title *"><input className={inputCls} value={edit.data.title} onChange={(e) => setEdit({ ...edit, data: { ...edit.data, title: e.target.value } })} data-testid="job-title-input" /></Field>
            <Field label="Location"><input className={inputCls} value={edit.data.location} onChange={(e) => setEdit({ ...edit, data: { ...edit.data, location: e.target.value } })} placeholder="e.g. Tarapur Works / Mumbai" data-testid="job-location-input" /></Field>
            <Field label="Employment Type">
              <select className={inputCls} value={edit.data.type} onChange={(e) => setEdit({ ...edit, data: { ...edit.data, type: e.target.value } })} data-testid="job-type-select">
                {["Full-time", "Contract", "Internship"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Department"><input className={inputCls} value={edit.data.department || ""} onChange={(e) => setEdit({ ...edit, data: { ...edit.data, department: e.target.value } })} placeholder="e.g. Engineering / QA-QC / Projects" data-testid="job-department-input" /></Field>
            <Field label="Required Experience"><input className={inputCls} value={edit.data.experience || ""} onChange={(e) => setEdit({ ...edit, data: { ...edit.data, experience: e.target.value } })} placeholder="e.g. 5–8 years" data-testid="job-experience-input" /></Field>
            <Field label="Qualification"><input className={inputCls} value={edit.data.qualification || ""} onChange={(e) => setEdit({ ...edit, data: { ...edit.data, qualification: e.target.value } })} placeholder="e.g. B.E. Mechanical" data-testid="job-qualification-input" /></Field>
            <Field label="Number of Vacancies"><input className={inputCls} value={edit.data.vacancies || ""} onChange={(e) => setEdit({ ...edit, data: { ...edit.data, vacancies: e.target.value } })} placeholder="e.g. 2" data-testid="job-vacancies-input" /></Field>
            <Field label="Closing Date"><input type="date" className={inputCls} value={edit.data.closing_date || ""} onChange={(e) => setEdit({ ...edit, data: { ...edit.data, closing_date: e.target.value } })} data-testid="job-closing-input" /></Field>
            <div className="sm:col-span-2"><Field label="Description"><textarea rows={4} className={inputCls} value={edit.data.description} onChange={(e) => setEdit({ ...edit, data: { ...edit.data, description: e.target.value } })} data-testid="job-desc-input" /></Field></div>
            <Field label="Responsibilities" hint="One per line."><textarea rows={5} className={inputCls} value={edit.respText} onChange={(e) => setEdit({ ...edit, respText: e.target.value })} data-testid="job-resp-input" /></Field>
            <Field label="Requirements" hint="One per line."><textarea rows={5} className={inputCls} value={edit.reqText} onChange={(e) => setEdit({ ...edit, reqText: e.target.value })} data-testid="job-req-input" /></Field>
            <div className="sm:col-span-2"><Field label="Skills Required" hint="One per line."><textarea rows={3} className={inputCls} value={edit.skillText || ""} onChange={(e) => setEdit({ ...edit, skillText: e.target.value })} data-testid="job-skills-input" /></Field></div>
            <div className="sm:col-span-2 flex justify-end gap-3 border-t border-slate-200 pt-4">
              <Btn variant="ghost" onClick={() => setEdit(null)}>Cancel</Btn>
              <Btn variant="dark" onClick={() => save(false)} disabled={busy} data-testid="job-save-btn">{busy && <Loader2 size={13} className="animate-spin" />} Save Draft</Btn>
              <Btn onClick={() => save(true)} disabled={busy} data-testid="job-publish-btn">Save & Publish</Btn>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!del} title="Delete this job opening?" message={`"${del?.data?.title}" will be permanently removed.`} onConfirm={doDelete} onClose={() => setDel(null)} busy={busy} />
    </div>
  );
};

export default CareersAdmin;
